// Landing-page composition, split out of src/main.jsx so the build-time
// prerender can import the tree on its own. The entry keeps the analytics
// imports and the mount; everything that renders lives here, which is what
// scripts/prerender.mjs needs — importing src/main.jsx on the server would
// pull in posthog-js and the LinkedIn tag for nothing.

import React from 'react';
import { t } from './i18n/useT.js';
import { localizedHref } from './i18n/bootstrap.js';

import { ModalRoot } from './Forms.jsx';
import { DemoVideoModalRoot } from './DemoVideoModalRoot.jsx';
import { CookieConsentRoot } from './CookieConsent.jsx';
import { SiteNav } from './Shell.jsx';
import { Hero } from './HeroAndReport.jsx';
import {
  ProblemSection,
  SolutionSection,
  HowItWorksSection,
  EarlyAccessSection,
  FaqSection,
  FinalCtaSection,
  Footer,
} from './Sections.jsx';
import {
  BrowserFrame,
  ClientQuestionCard,
  InvoiceWaterfall,
} from './DashboardWidgets.jsx';
import { ScopeWorthTweaks } from './Tweaks.jsx';
import { CAPACITY } from './CapacityModel.js';

// The former LandingReportAnatomy section was absorbed into the hero (the
// HeroV3 report frame IS the report anatomy, with evidence-on-hover) — see
// scripts/HeroAndReport.jsx. Removing it also restores the light/dark
// section rhythm after the dark hero.

function LandingInvoiceDefense() {
  return (
    <section className="sw-section swd-section">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-amber">{t('landing.invoiceDefense.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{t('landing.invoiceDefense.title')}</h2>
          <p className="sw-lead">{t('landing.invoiceDefense.lead')}</p>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/invoice">
        <div className="swd-defense-strip">
          {(() => { const tiles = t('landing.defenseTiles'); const tones = ['is-amber', 'is-red', 'is-red', 'is-amber']; return tiles.map((tile, i) => (
            <div key={i} className={`swd-defense-tile ${tones[i]}`}>
              <div className="swd-defense-tile-l">{tile.label}</div>
              <div className="swd-defense-tile-v">{tile.value}</div>
              <div className="swd-defense-tile-d">{tile.desc}</div>
            </div>
          )); })()}
        </div>
        <div className="swd-stack">
          <ClientQuestionCard/>
          <InvoiceWaterfall/>
        </div>
        </BrowserFrame>
      </div>
    </section>
  );
}

// "What changes for you" — quantified buyer outcomes, drawn from the same
// sample-report numbers used across the site (so the promise and the proof
// match). Copy lives in i18n under `landing.outcomes.items`.
function LandingOutcomes() {
  return (
    <section className="sw-section sw-section-light">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow sw-eyebrow-teal">{t('landing.outcomes.eyebrow')}</div>
          <h2 className="sw-h2">{t('landing.outcomes.title')}</h2>
          <p className="sw-lead">{t('landing.outcomes.lead')}</p>
        </div>
        <div className="sw-outcomes-grid">
          {t('landing.outcomes.items').map(o => (
            <div className="sw-outcome" key={o.tag}>
              <div className="sw-outcome-tag">{o.tag}</div>
              <div className="sw-outcome-from">{o.from}</div>
              <div className="sw-outcome-arrow" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </div>
              <div className="sw-outcome-to">{o.to}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function fmtEur(n) {
  return '$' + Math.round(n).toLocaleString('en-GB');
}

// Landing teaser for the Capacity Cost Simulator. The landing page keeps the
// promise scannable and links out to the product page, like every other
// section.
function LandingCapacityCalculator() {
  const baseline = CAPACITY.scenarioById('base');
  const scenA = CAPACITY.scenarioById('a');
  const extra = scenA.totalCost - baseline.totalCost;
  const tiles = [
    { cls: '',       label: t('capacityWidget.kpiBaseline'),  value: baseline.weeks.toFixed(1), sub: fmtEur(baseline.totalCost) + ' · ' + t('capacityWidget.currentTeam') },
    { cls: 'is-rec', label: scenA.name,                       value: scenA.weeks.toFixed(1),    sub: scenA.sub },
    { cls: 'is-warn', label: t('capacityWidget.kpiTimeSaved'), value: scenA.timeSaved.toFixed(1), sub: fmtEur(extra) + ' ' + t('landing.capacity.extraLabel') },
  ];
  return (
    <section className="sw-section sw-capacity" id="capacity">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow sw-capacity-eyebrow">{t('landing.capacity.eyebrow')}</div>
          <h2 className="sw-h2">{t('landing.capacity.title')} <span className="sw-capacity-title-accent">{t('landing.capacity.titleAccent')}</span></h2>
          <p className="sw-lead">{t('landing.capacity.lead')}</p>
        </div>
        <div className="sw-capacity-teaser">
          <div className="sw-capacity-kpis sw-capacity-kpis--teaser">
            {tiles.map((k, i) => (
              <div key={i} className={`sw-capacity-kpi ${k.cls}`}>
                <div className="sw-capacity-kpi-l">{k.label}</div>
                <div className="sw-capacity-kpi-v">{k.value}<span className="sw-capacity-kpi-unit">{t('capacityWidget.wks')}</span></div>
                <div className="sw-capacity-kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>
          <div className="sw-capacity-teaser-cta">
            <a className="sw-btn sw-btn-primary" href={localizedHref('/product')}>
              <span className="sw-btn-label">{t('landing.capacity.seeCapabilities')}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function App() {
  React.useEffect(() => {
    const els = document.querySelectorAll('.sw-reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SiteNav currentPath="/" variant="transparent" />
      <Hero />
      <div className="sw-reveal"><ProblemSection /></div>
      <div className="sw-reveal"><SolutionSection /></div>
      <div className="sw-reveal"><LandingOutcomes /></div>
      <div className="sw-reveal"><LandingInvoiceDefense /></div>
      <div className="sw-reveal"><HowItWorksSection /></div>
      <div className="sw-reveal"><LandingCapacityCalculator /></div>
      <div className="sw-reveal"><EarlyAccessSection /></div>
      <div className="sw-reveal"><FaqSection /></div>
      <div className="sw-reveal"><FinalCtaSection /></div>
      <Footer />
      <ModalRoot />
      <DemoVideoModalRoot />
      <CookieConsentRoot />
      {/* Design-iteration panel only: its effects overwrite the hero H1 with
          the hardcoded English headline, which clobbers localized copy. */}
      {import.meta.env.DEV ? <ScopeWorthTweaks /> : null}
    </>
  );
}
